import {
  scrollToError,
  scrollToLayoutV2ElementWithOffset,
} from '@shared/utils';
import useUpdateOpportunity from '@modules/opportunities/entities/opportunity/mutation/useUpdateOpportunity';
import { CreateOpportunityApplicantDTO } from '@modules/opportunities/entities/opportunity/api/dto/createOpportunity.dto';
import useUpdateClient from '@modules/clients/entities/client/mutation/useUpdateClient';
import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { useForm, zodResolver } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
  APPLICANT_TYPE,
  MORTGAGE_USAGE,
  OPPORTUNITY_STATUS,
} from '@modules/opportunities/entities/opportunity/const';
import {
  Button, Flex, Box, Text
} from '@huspy/briks-web';
import {
  useOpportunityFormErrorHandler,
  useClientFormErrorHandler,
} from '@modules/opportunities/presentation/hooks/useHandleErrors';
import { useLocation } from '@tanstack/react-router';
import {
  useContext, useEffect, useState
} from 'react';
import { UnsavedChangesSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/UnsavedChangesSlido';
import { DeleteApplicantSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/DeleteApplicantSlido';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { EditRestrictionMessage } from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/components/EditRestrictionMessage';
import { caseBasicInfoSchemaUAE } from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/validators/ae';
import { CaseCreationContext } from '@modules/opportunities/presentation/pages/CaseCreation';
import { mapOpportunityDataToCaseBasicInfoFormUAE } from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/mappers';
import ScrollArea from '@components/v2/ScrollArea';
import { convertYearsAndMonthsToTotalMonths } from '@shared/utils/date-utils';
import { useDisclosure } from '@mantine/hooks';
import { BUSINESS_IDENTIFIER } from '@modules/core/api/const';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import { MainApplicantInfo } from './components/MainApplicantInfo';
import { MortgageInfo } from './components/MortgageInfo';
import OtherApplicantInfo from './components/OtherApplicantInfo';
import { AssistedStatusInfo } from './components/AssistedStatusInfo';

export const CaseBasicInfoFormUAE = ({ opportunityData }: {
  opportunityData: Opportunity<'AE'>;
}) => {
  const { user } = usePersistUser();
  const { goToNextState } = useContext(CaseCreationContext);
  const { hash } = useLocation();

  const {
    mutateAsync: updateOpportunity,
    isPending: isUpdateOpportunityLoading,
    error: updateOppError,
  } = useUpdateOpportunity(goToNextState);
  const {
    mutateAsync: updateClient,
    isPending: isUpdatingClient,
    error: updateClientError,
  } = useUpdateClient(opportunityData.client_external_id!, user?.brokerageId);
  const [opened, { close, open }] = useDisclosure(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: mapOpportunityDataToCaseBasicInfoFormUAE(opportunityData),
    validate: zodResolver(caseBasicInfoSchemaUAE),
  });

  const { t } = useTranslation();

  const mainApplicant = opportunityData.applicants.find(
    (item) => item.applicant_type === APPLICANT_TYPE.mainApplicant
  );
  const otherApplicant = opportunityData.applicants.find(
    (item) => item.applicant_type !== APPLICANT_TYPE.mainApplicant
  );
  const [currentApplicantType, setCurrentApplicantType] = useState(
    otherApplicant?.applicant_type ?? APPLICANT_TYPE.mainApplicant
  );

  const isLoading = isUpdatingClient || isUpdateOpportunityLoading;

  const handleSave = async () => {
    const data = form.getValues();
    // Track attempt
    trackAmplitudeEvent(
      USER_EVENTS.CASE.CASE_BASIC_INFORMATION_SAVE_ATTEMPTED,
      {
        addToContext: true,
        case_id: opportunityData.opportunity_external_id,
        client_id: opportunityData.client_external_id ?? undefined,
        banks: opportunityData.bank_applications.map(
          (bankApplication) => bankApplication?.bank_details?.external_id
        ),
      }
    );

    // Main applicant
    const applicants = [
      {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        mobile: data.mobile,
        external_id: mainApplicant?.opportunity_applicant_external_id,
        dob: data.dob,
        employment_status: data.employment_status,
        citizen_status: data.citizen_status,
        fixed_monthly_salary: data.salary,
        nationality: data.nationality,
      }
    ] as CreateOpportunityApplicantDTO<'AE'>[];

    if (
      data.application_type !== APPLICANT_TYPE.mainApplicant
      && data.applicant_first_name
      && data.applicant_last_name
    ) {
      applicants.push({
        external_id: otherApplicant?.opportunity_applicant_external_id,
        first_name: data.applicant_first_name,
        last_name: data.applicant_last_name,
        applicant_type: data.application_type,
        email: data.applicant_email,
        mobile: data.applicant_mobile,
        dob: data.applicant_dob,
        employment_status: data.applicant_employment_status,
        citizen_status: data.applicant_citizen_status,
        fixed_monthly_salary: data.applicant_salary,
        nationality: data.applicant_nationality,
        relationship_to_main: data.applicant_relationship_to_main,
      });
    }

    const mortgageLengthInMonths = convertYearsAndMonthsToTotalMonths(
      data.mortgage_years ?? 0,
      data.mortgage_months ?? 0
    );
    const bankApplicationBankIdMapper = Object.fromEntries(
      opportunityData.bank_applications
        .filter((item) => item.status !== BANK_APPLICATION_STATUS.draft)
        .map((value) => [value.bank_details.external_id, value])
    );

    const promises = [
      updateOpportunity({
        opportunityId: opportunityData.opportunity_external_id,
        body: {
          amount: data.amount,
          is_fee_financed: data.is_fee_financed,
          property_status: data.property_status,
          property_value: data.property_value,
          state: data.state!,
          type_of_transaction: data.type_of_transaction!,
          is_assisted: data.is_assisted,
          applicants,
          banks: data.selected_banks.map((sb) => ({
            external_id: sb.id,
            mortgage_type: sb.mortgageType!,
            mortgage_length_months:
              bankApplicationBankIdMapper[sb.id]?.mortgage_length_months
              ?? mortgageLengthInMonths,
          })),
        },
      })
    ];

    const shouldUpdateClient = form.isDirty('email')
      || form.isDirty('mobile')
      || form.isDirty('first_name')
      || form.isDirty('last_name');

    if (shouldUpdateClient) {
      promises.push(
        updateClient({
          first_name: data.first_name!,
          last_name: data.last_name!,
          email: data.email,
          mobile: data.mobile,
        })
      );
    }

    form.resetDirty();
    await Promise.all(promises);

    // Track success
    trackAmplitudeEvent(
      USER_EVENTS.CASE.CASE_BASIC_INFORMATION_SAVE_COMPLETED,
      { useContext: true }
    );
  };

  useOpportunityFormErrorHandler({ opportunityErrors: updateOppError, form });
  useClientFormErrorHandler({ clientErrors: updateClientError, form });

  const isSomeCasesSubmitted = opportunityData.status !== OPPORTUNITY_STATUS.draft
    || opportunityData.bank_applications.some(
      ({ status }) => status !== BANK_APPLICATION_STATUS.draft
    );

  useEffect(() => {
    if (hash) {
      scrollToLayoutV2ElementWithOffset(hash, 80);
    }
  }, [hash]);

  form.watch('application_type', ({ value }) => {
    setCurrentApplicantType(value);
  });

  const handleSubmit = () => {
    const { errors, hasErrors } = form.validate();
    if (hasErrors) {
      scrollToError(errors);
      return;
    }
    if (!form.isDirty()) {
      goToNextState();
      return;
    }
    if (
      otherApplicant
      && currentApplicantType === APPLICANT_TYPE.mainApplicant
    ) {
      open();
      return;
    }
    handleSave();
  };

  return (
    <ScrollArea>
      <form onSubmit={ (e) => e.preventDefault() }>
        <Flex gap='8' direction='column'>
          <Flex direction='column' gap='1'>
            <Text size='2xl' weight='medium'>
              {t('clients.formSections.basicInfoTile')}
            </Text>
            <Text size='lg' color='neutral.500'>
              {t('clients.formSections.basicInfoDescription')}
            </Text>
          </Flex>
          <Box bg='white' p='6' borderRadius='6'>
            <Flex direction='column' gap='4'>
              <AssistedStatusInfo
                form={ form }
                isCommercial={ opportunityData.mortgage_usage === MORTGAGE_USAGE.COMMERCIAL }
                isSomeCasesSubmitted={ isSomeCasesSubmitted }
              />
              <MainApplicantInfo
                form={ form }
                isSomeCasesSubmitted={ isSomeCasesSubmitted }
                businessUnit={ BUSINESS_IDENTIFIER.AE_HUSPY }
              />
              <MortgageInfo
                form={ form }
                isSomeCasesSubmitted={ isSomeCasesSubmitted }
                bankApplications={ opportunityData.bank_applications }
                hash={ hash }
                businessUnit={ BUSINESS_IDENTIFIER.AE_HUSPY }
              />
              {currentApplicantType !== APPLICANT_TYPE.mainApplicant && (
                <OtherApplicantInfo
                  form={ form }
                  isSomeCasesSubmitted={ isSomeCasesSubmitted }
                  businessUnit={ BUSINESS_IDENTIFIER.AE_HUSPY }
                />
              )}
            </Flex>
          </Box>
          <EditRestrictionMessage isVisible={ isSomeCasesSubmitted } />
          <Flex justify='end' gap='2'>
            <Button
              type='submit'
              disabled={ isLoading }
              loading={ isLoading }
              variant={ isLoading ? 'secondary' : 'primary' }
              data-test='proceed-btn'
              onClick={ handleSubmit }
            >
              {t('clients.formSections.proceedToBanksBtn')}
            </Button>
          </Flex>
        </Flex>
      </form>
      <UnsavedChangesSlido
        isDirty={ form.isDirty() }
        save={ handleSave }
        isLoading={ isLoading }
      />
      {otherApplicant && (
        <DeleteApplicantSlido
          opportunityId={ opportunityData.opportunity_external_id }
          applicantId={ otherApplicant?.opportunity_applicant_external_id! }
          opened={ opened }
          close={ close }
          updateOpportunity={ handleSave }
        />
      )}
    </ScrollArea>
  );
};
